import React, { FC, useContext, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { isBrowser } from '@agria/utils';
import { ImageDataLike } from 'gatsby-plugin-image';
import { Container } from '../../container/Container';
import { LinkTo } from '../../linkTo/LinkTo';
import { Contact } from '../../icon/icons/Contact';
import { Typography } from '../../typography/Typography';
import { Logo } from '../../svg';
import { LogoSmall } from '../../svg/svgs/LogoSmall';
import { useBackgroundColor } from '../../../utils';
import tokens from './tokens';
import { Image } from '../../Image/Image';
import { ExperimentsContext } from '../../../context/ExperimentsContext';

// Interface
export interface SimpleHeaderProps {
  companyName?: string;
  contactNumber?: string;
  logoLink?: string;
  disableContact?: boolean;
  secondaryLogo?: ImageDataLike; // Logo in top right corner - this overides the contact number
  isBrochureware?: boolean;
  centerContent?: React.ReactNode; // Optional content to display centered between logo and contact
}

// Styles
const StyledSimpleHeader = styled.header`
  ${({ theme: { space } }) => css`
    padding: ${space.fluidSmall} 0;
    ${useBackgroundColor(tokens.backgroundColor || 'utilityWhite')};
  `};
`;

const StyledContainer = styled(Container)<{ hasCenterContent: boolean }>`
  ${({ theme: { space }, hasCenterContent }) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: ${hasCenterContent ? 'wrap' : 'nowrap'};
    row-gap: ${hasCenterContent ? space.xxSmall : 0};
  `};
`;

const StyledCenterContent = styled.div`
  ${({ theme: { media } }) => css`
    order: 3;
    flex: 0 0 100%;
    display: flex;
    justify-content: center;

    @media (min-width: ${media.medium}) {
      order: 0;
      flex: 0 0 auto;
    }
  `};
`;

const StyledLogo = styled(LinkTo)<{ isAGRIA126Variation: boolean }>`
  ${({ theme: { space, colors }, isAGRIA126Variation }) => css`
    margin-right: ${isAGRIA126Variation ? 0 : space.medium};

    ${tokens.includeInverseFocus &&
    css`
      &:focus-visible {
        background-color: ${colors.brandTwo100};
        color: ${colors.utilityWhite};
        padding: 4px;
        margin: -4px;
      }
    `}
  `};
`;

const StyledLogoMobile = styled.span`
  ${({ theme: { media } }) => css`
    @media (min-width: ${media.medium}) {
      display: none;
    }
  `};
`;

const StyledLogoDesktop = styled.span`
  ${({ theme: { maxMedia } }) => css`
    @media (max-width: ${maxMedia.medium}) {
      display: none;
    }
  `};
`;

const StyledContact = styled.div<{ isVisible: boolean }>`
  ${({ theme: { space, maxMedia }, isVisible }) => css`
    display: ${isVisible ? 'flex' : 'none'};
    align-items: center;
    gap: ${space.xxSmall};

    @media (max-width: ${maxMedia.medium}) {
      display: none;
    }

    a {
      text-decoration: none;
    }
  `};
`;

const StyledContactNumberMobile = styled.a<{ isVisible: boolean }>`
  ${({ theme: { media }, isVisible }) => css`
    display: ${isVisible ? 'flex' : 'none'};
    border: none;
    padding: 0;
    @media (min-width: ${media.medium}) {
      display: none;
    }
  `};
`;

// Utility Function
const getUKDateTimeParts = () => {
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    hour: 'numeric',
    hour12: false,
    weekday: 'long',
  });

  const parts = formatter.formatToParts(new Date());
  let UKHour;
  let UKDayName;

  parts.forEach((part) => {
    if (part.type === 'hour') {
      UKHour = parseInt(part.value, 10);
    }
    if (part.type === 'weekday') {
      UKDayName = part.value;
    }
  });

  // Map weekday names to numbers
  const weekdayMap = {
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
    Sunday: 7,
  };

  const UKDay = weekdayMap[UKDayName] || 1; // Default to 1 if not found

  return { UKHour, UKDay };
};

// Component
export const SimpleHeader: FC<SimpleHeaderProps> = ({
  companyName,
  contactNumber,
  logoLink = '/',
  disableContact = false,
  secondaryLogo,
  isBrochureware,
  centerContent,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { experiments } = useContext(ExperimentsContext);

  const isAGRIA126Variation =
    experiments?.AGRIA126 === 'variation' ||
    process.env.GATSBY_EXPERIMENT_AGRIA126 === 'variation';

  useEffect(() => {
    if (isBrowser) {
      const { UKHour, UKDay } = getUKDateTimeParts();

      // Business hours: Monday to Friday, 9 AM to 6 PM
      const isOpenState =
        UKDay >= 1 && UKDay <= 5 && UKHour >= 9 && UKHour < 18;

      setIsOpen(isOpenState);
    }
  }, []);

  const showContactNumber =
    !disableContact && isOpen && (tokens.includePhoneNumber || isBrochureware);
  return (
    <StyledSimpleHeader isBrochureware={isBrochureware}>
      <StyledContainer hasCenterContent={!!centerContent}>
        <StyledLogo
          isAGRIA126Variation={isAGRIA126Variation}
          to={logoLink}
          title={`${companyName} homepage`}
          aria-label={`${companyName} homepage`}
          state={{ reset: true }}
        >
          <StyledLogoMobile>
            <LogoSmall title={`${companyName} homepage`} aria-hidden="true" />
          </StyledLogoMobile>
          <StyledLogoDesktop>
            <Logo title={`${companyName} homepage`} aria-hidden="true" />
          </StyledLogoDesktop>
        </StyledLogo>
        {centerContent && (
          <StyledCenterContent>{centerContent}</StyledCenterContent>
        )}
        {secondaryLogo && secondaryLogo.image && (
          <Image
            src={secondaryLogo.image.localFile}
            alt={secondaryLogo.image.altText}
          />
        )}
        {!secondaryLogo && showContactNumber && (
          <>
            <StyledContactNumberMobile
              aria-label={`Call us${contactNumber && ` on ${contactNumber}`}`}
              href={`tel:${contactNumber?.replace(/\s/g, '')}`}
              isVisible={isOpen}
            >
              <Contact title="Call us" />
            </StyledContactNumberMobile>
            <StyledContact isVisible={isOpen}>
              <Contact
                title="Call us"
                color={tokens.color}
                aria-hidden="true"
              />
              <Typography variant="bodyMediumBold">
                <a href={`tel:${contactNumber?.replace(/\s/g, '')}`}>
                  Call us on {contactNumber}
                </a>
              </Typography>
            </StyledContact>
          </>
        )}
      </StyledContainer>
    </StyledSimpleHeader>
  );
};

export default SimpleHeader;
